/**
 * Bulk-import spec for the `Surveys` sheet of the organizer workbook
 * (ADR-054 — surveys are anonymous in v1; organizers read aggregates only).
 *
 * One row per survey. The bulk callable resolves `session` (a session
 * title reference for this event) and converts `scheduledAt` from an
 * event-local string to a UTC Timestamp admin-side before sending.
 *
 * `type` semantics map directly to `Survey.type`:
 *   - `rating` -> 1..5 scale, `options` ignored
 *   - `single_choice` -> requires non-empty `options` (comma-list)
 *   - `free_text` -> long-form response, `options` ignored
 *
 * `sendNotification` toggles the survey's announcement push. When true,
 * the dialog also requires `notificationTitle` + `notificationBody`. The
 * notification is created as a sibling doc by the bulk-create callable
 * (same pattern as the admin compose flow in `surveyService.createSurvey`).
 */
import { invalid, pending, type ImportSpec, type ParsedRow } from "./importSpec";
import { normalize } from "./importTypes";

export type SurveyTypeValue = "rating" | "single_choice" | "free_text";
export type SurveyScheduleMode = "now" | "scheduled";

const ALLOWED_TYPES: readonly SurveyTypeValue[] = [
  "rating",
  "single_choice",
  "free_text",
] as const;

const ALLOWED_SCHEDULE_MODES: readonly SurveyScheduleMode[] = [
  "now",
  "scheduled",
] as const;

const SCHEDULE_MODE_ALIASES: Record<string, SurveyScheduleMode> = {
  now: "now",
  immediate: "now",
  scheduled: "scheduled",
  later: "scheduled",
};

/** Match "YYYY-MM-DD HH:MM" or "YYYY-MM-DDTHH:MM" (with optional seconds). */
const LOCAL_DATETIME_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::\d{2})?$/;

function parseLocalDateTime(raw: string | undefined): string | null {
  const value = normalize(raw);
  if (!value) return null;
  const m = LOCAL_DATETIME_PATTERN.exec(value);
  if (!m) return null;
  const [, y, mo, d, h, mi] = m;
  return `${y}-${mo}-${d}T${h}:${mi}`;
}

function parseBoolean(raw: string | undefined): boolean | null {
  const value = normalize(raw).toLowerCase();
  if (!value) return false;
  if (value === "true" || value === "yes" || value === "y" || value === "1") {
    return true;
  }
  if (value === "false" || value === "no" || value === "n" || value === "0") {
    return false;
  }
  return null;
}

function splitCommaList(raw: string | undefined): string[] {
  return normalize(raw)
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export interface RawSurveyImportRow extends Record<string, string | undefined> {
  question?: string;
  type?: string;
  options?: string;
  session?: string;
  scheduleMode?: string;
  scheduledAt?: string;
  sendNotification?: string;
  notificationTitle?: string;
  notificationBody?: string;
  // ── Power BI dashboard presentation labels (admin survey form parity) ──
  ratingAxisLabel?: string;
  ratingScaleMin?: string;
  ratingScaleMax?: string;
  optionAxisLabel?: string;
  responseLabel?: string;
  chartSubtitle?: string;
}

export interface ParsedSurveyImportRow {
  question: string;
  type: SurveyTypeValue;
  /** Comma-list parsed into an array; empty for non-single_choice surveys. */
  options: string[];
  /** Session title reference (case-insensitive match within event). Null = event-scoped. */
  session: string | null;
  scheduleMode: SurveyScheduleMode;
  /** Event-local naive datetime "YYYY-MM-DDTHH:mm" when scheduleMode=scheduled; null otherwise. */
  scheduledAtLocal: string | null;
  sendNotification: boolean;
  notificationTitle: string | null;
  notificationBody: string | null;
  /**
   * Power BI presentation labels. All optional; null means "use the
   * dashboard default." Match the admin survey form fields per the
   * surveyDocumentSchema (Survey.ratingAxisLabel et al).
   *
   * - `ratingAxisLabel`: y-axis label on the rating-survey histogram.
   * - `ratingScaleMin` / `ratingScaleMax`: endpoint labels for the 1-5
   *   rating scale (e.g. "Poor" / "Excellent").
   * - `optionAxisLabel`: x-axis label on the single-choice bar chart.
   * - `responseLabel`: column header for the count axis ("Responses" by
   *   default).
   * - `chartSubtitle`: free-text caption beneath the chart title.
   */
  ratingAxisLabel: string | null;
  ratingScaleMin: string | null;
  ratingScaleMax: string | null;
  optionAxisLabel: string | null;
  responseLabel: string | null;
  chartSubtitle: string | null;
}

export const surveyImportSpec: ImportSpec<
  RawSurveyImportRow,
  ParsedSurveyImportRow
> = {
  sheetName: "Surveys",
  purpose:
    "Anonymous attendee surveys (ratings, polls, open-ended feedback). One row per question. Optional companion push notification announces each survey.",
  columns: [
    { key: "question", note: "required - the prompt shown to attendees" },
    {
      key: "type",
      note: `required - one of: ${ALLOWED_TYPES.join(", ")}`,
    },
    {
      key: "options",
      note:
        "comma-separated choices for single_choice surveys only. Leave blank for rating / free_text.",
    },
    {
      key: "session",
      note:
        "optional - session title to scope this survey to. Leave blank for event-wide surveys.",
    },
    {
      key: "scheduleMode",
      note: "required - 'now' to open immediately, 'scheduled' (or 'later') for a future open",
    },
    {
      key: "scheduledAt",
      note:
        "YYYY-MM-DD HH:MM in event-local time. Required when scheduleMode=scheduled.",
    },
    {
      key: "sendNotification",
      note: "true / false - whether to create a companion push announcing the survey",
    },
    {
      key: "notificationTitle",
      note: "required when sendNotification=true",
    },
    {
      key: "notificationBody",
      note: "required when sendNotification=true",
    },
    {
      key: "ratingAxisLabel",
      note:
        "optional - rating-survey y-axis label on the Power BI chart",
    },
    {
      key: "ratingScaleMin",
      note:
        "optional - left endpoint label for the 1-5 scale (e.g. 'Poor')",
    },
    {
      key: "ratingScaleMax",
      note:
        "optional - right endpoint label for the 1-5 scale (e.g. 'Excellent')",
    },
    {
      key: "optionAxisLabel",
      note:
        "optional - single_choice-survey x-axis label on the Power BI chart",
    },
    {
      key: "responseLabel",
      note: "optional - count-axis label, defaults to 'Responses'",
    },
    {
      key: "chartSubtitle",
      note:
        "optional - free-text caption shown beneath the chart title",
    },
  ],
  example: {
    question: "How would you rate the keynote?",
    type: "rating",
    options: "",
    session: "Opening Keynote",
    scheduleMode: "now",
    scheduledAt: "",
    sendNotification: "true",
    notificationTitle: "Quick feedback: Opening Keynote",
    notificationBody:
      "One-tap rating: how would you rate the opening keynote?",
    ratingAxisLabel: "Keynote rating distribution",
    ratingScaleMin: "Poor",
    ratingScaleMax: "Excellent",
    optionAxisLabel: "",
    responseLabel: "Responses",
    chartSubtitle: "Live results, all attendees",
  },
  identity: {
    idPrefix: "survey",
    label: (row) => row.question,
    naturalKey: (row) => [
      row.question,
      row.session ?? "event",
      row.scheduleMode,
      row.scheduledAtLocal ?? "now",
    ],
  },
  validate(raw, lineNumber) {
    const question = normalize(raw.question);
    const typeRaw = normalize(raw.type).toLowerCase();
    const options = splitCommaList(raw.options);
    const session = normalize(raw.session) || null;
    const scheduleModeRaw = normalize(raw.scheduleMode).toLowerCase();
    const scheduleMode = SCHEDULE_MODE_ALIASES[scheduleModeRaw];
    const scheduledAtLocal = parseLocalDateTime(raw.scheduledAt);
    const sendNotificationParsed = parseBoolean(raw.sendNotification);
    const notificationTitle = normalize(raw.notificationTitle) || null;
    const notificationBody = normalize(raw.notificationBody) || null;
    const ratingAxisLabel = normalize(raw.ratingAxisLabel) || null;
    const ratingScaleMin = normalize(raw.ratingScaleMin) || null;
    const ratingScaleMax = normalize(raw.ratingScaleMax) || null;
    const optionAxisLabel = normalize(raw.optionAxisLabel) || null;
    const responseLabel = normalize(raw.responseLabel) || null;
    const chartSubtitle = normalize(raw.chartSubtitle) || null;

    const base: Omit<ParsedRow<ParsedSurveyImportRow>, "status" | "message"> = {
      lineNumber,
      data: {
        question,
        type: (typeRaw as SurveyTypeValue) || "rating",
        options,
        session,
        scheduleMode: (scheduleMode ?? "now") as SurveyScheduleMode,
        scheduledAtLocal,
        sendNotification: sendNotificationParsed === true,
        notificationTitle,
        notificationBody,
        ratingAxisLabel,
        ratingScaleMin,
        ratingScaleMax,
        optionAxisLabel,
        responseLabel,
        chartSubtitle,
      },
    };

    if (!question) return invalid(base, "Question is required");

    if (!(ALLOWED_TYPES as readonly string[]).includes(typeRaw)) {
      return invalid(
        base,
        `Invalid type "${typeRaw}". Must be one of: ${ALLOWED_TYPES.join(", ")}`,
      );
    }

    if (typeRaw === "single_choice" && options.length < 2) {
      return invalid(
        base,
        "single_choice surveys need at least 2 comma-separated options.",
      );
    }

    if (!scheduleMode) {
      return invalid(
        base,
        `Invalid scheduleMode "${scheduleModeRaw}". Must be one of: ${ALLOWED_SCHEDULE_MODES.join(", ")}`,
      );
    }
    if (scheduleMode === "scheduled" && !scheduledAtLocal) {
      return invalid(
        base,
        'scheduledAt must be in "YYYY-MM-DD HH:MM" format when scheduleMode=scheduled.',
      );
    }

    if (sendNotificationParsed === null) {
      return invalid(
        base,
        `Invalid sendNotification "${raw.sendNotification}". Must be true or false.`,
      );
    }
    if (sendNotificationParsed) {
      if (!notificationTitle) {
        return invalid(
          base,
          "notificationTitle is required when sendNotification=true.",
        );
      }
      if (!notificationBody) {
        return invalid(
          base,
          "notificationBody is required when sendNotification=true.",
        );
      }
    }

    return pending({
      ...base,
      data: {
        ...base.data,
        type: typeRaw as SurveyTypeValue,
        // Non-single_choice surveys clear options so the doc shape stays
        // tidy and the server doesn't have to filter on read.
        options: typeRaw === "single_choice" ? options : [],
      },
    });
  },
};
